import React, { useState, useRef, useEffect } from 'react';
import { audioEngine } from '../features/audio/audioEngine';

// Custom Hook for Drag Logic
const useDraggableFader = (initialValue, onChange) => {
    const [value, setValue] = useState(initialValue);
    const trackRef = useRef(null);
    const isDragging = useRef(false);
    const startY = useRef(0);
    const startVal = useRef(0);

    const handleMouseDown = (e) => {
        e.stopPropagation(); // Prevent bubbling
        e.preventDefault();  // Prevent text selection
        isDragging.current = true;
        startY.current = e.clientY;
        startVal.current = value;

        // Global Listeners for reliable drag
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e) => {
        if (!isDragging.current || !trackRef.current) return;

        const trackRect = trackRef.current.getBoundingClientRect();
        const trackHeight = trackRect.height;

        // Calculate Delta
        const deltaY = startY.current - e.clientY; // Up is positive
        const deltaVal = deltaY / trackHeight;

        // Update
        let newValue = startVal.current + deltaVal;
        newValue = Math.max(0, Math.min(1, newValue));

        setValue(newValue);
        onChange(newValue);
    };

    const handleMouseUp = () => {
        isDragging.current = false;
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
    };

    return { value, trackRef, handleMouseDown };
};

const Fader = ({ label, valueProp, onChange, color = "gray" }) => {
    // We sync local drag state with prop but allow local override during drag
    // Actually simpler to just use prop controlled mode if parent manages state well.
    // But for physics, lets use the hook logic but driven by parent props slightly?
    // Let's modify hook to update parent.

    const trackRef = useRef(null);
    const isDragging = useRef(false);
    const startY = useRef(0);
    const startVal = useRef(0);

    const handleMouseDown = (e) => {
        e.preventDefault();
        isDragging.current = true;
        startY.current = e.clientY;
        startVal.current = valueProp;
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e) => {
        if (!isDragging.current) return;
        const trackHeight = trackRef.current.offsetHeight - 40; // Subtract padding/thumb height approx
        // Safer:
        const rect = trackRef.current.getBoundingClientRect();
        const usableHeight = rect.height - 48; // thumb height

        const deltaY = startY.current - e.clientY;
        const deltaVal = deltaY / usableHeight;

        let newValue = startVal.current + deltaVal;
        newValue = Math.max(0, Math.min(1, newValue));
        onChange(newValue);
    };

    const handleMouseUp = () => {
        isDragging.current = false;
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
    };

    const colorMap = {
        gray: "bg-gray-400",
        blue: "bg-cyan-500",
        green: "bg-emerald-500",
        yellow: "bg-amber-400"
    };
    const thumbColor = colorMap[color] || colorMap.gray;

    return (
        <div className="flex flex-col items-center h-full gap-3 group select-none">
            {/* LED Meter */}
            <div className="flex flex-col gap-[2px] w-4 bg-black/60 p-[2px] rounded border border-white/10 shadow-inner">
                {[...Array(12)].map((_, i) => (
                    <div
                        key={i}
                        className={`h-1 w-full rounded-[1px] ${(11 - i) / 11 <= valueProp + 0.05
                            ? i === 0 ? 'bg-red-500 shadow-[0_0_5px_red]'
                                : i < 3 ? 'bg-yellow-500'
                                    : 'bg-green-500 shadow-[0_0_2px_green]'
                            : 'bg-[#151515]'
                            }`}
                    />
                ))}
            </div>

            {/* Fader Track */}
            <div ref={trackRef} className="relative flex-1 w-12 bg-[#0c0c0c] rounded-lg border border-white/5 shadow-[inset_0_2px_5px_black] py-2">
                {/* Guide Line */}
                <div className="absolute top-4 bottom-4 left-1/2 -translate-x-1/2 w-[2px] bg-[#222]" />

                {/* Thumb (Draggable) */}
                <div
                    onMouseDown={handleMouseDown}
                    className={`absolute left-1 right-1 h-10 ${thumbColor} rounded-sm shadow-[0_4px_6px_black,inset_0_1px_0_rgba(255,255,255,0.4)] border-t border-white/30 cursor-grab active:cursor-grabbing z-10 flex items-center justify-center hover:brightness-110`}
                    style={{
                        bottom: `calc(${valueProp * 100}% - ${valueProp * 40}px + 2px)`
                    }}
                >
                    {/* Grip Lines */}
                    <div className="flex flex-col gap-1 w-full px-2 opacity-50">
                        <div className="h-[1px] bg-black" />
                        <div className="h-[1px] bg-black" />
                        <div className="h-[1px] bg-black" />
                    </div>
                </div>
            </div>

            {/* Label */}
            <div className="h-6 w-full flex items-center justify-center">
                <span className="text-[10px] font-bold text-gray-400 bg-black/30 px-1 rounded border border-white/5 w-full text-center">
                    {label}
                </span>
            </div>
        </div>
    );
}

export default function Mixer() {
    const [levels, setLevels] = useState({
        master: 0.8,
        mic: 0.8,
        harmony3: 0.0,
        harmony5: 0.0,
        reverb: 0.3,
        delay: 0.0,
        width: 0.5,
        speed: 1.0
    });

    const updateLevel = (id, val) => {
        setLevels(prev => ({ ...prev, [id]: val }));
        if (id === 'master') audioEngine.setVolume(val);
        if (id === 'harmony3') audioEngine.setHarmonyLevel(3, val);
        if (id === 'harmony5') audioEngine.setHarmonyLevel(5, val);
    };

    return (
        <div className="grid grid-cols-8 gap-2 h-full px-2">
            <Fader label="MIC" valueProp={levels.mic} onChange={(v) => updateLevel('mic', v)} color="gray" />
            <Fader label="3RD" valueProp={levels.harmony3} onChange={(v) => updateLevel('harmony3', v)} color="blue" />
            <Fader label="5TH" valueProp={levels.harmony5} onChange={(v) => updateLevel('harmony5', v)} color="blue" />
            <Fader label="REV" valueProp={levels.reverb} onChange={(v) => updateLevel('reverb', v)} color="green" />
            <Fader label="DLY" valueProp={levels.delay} onChange={(v) => updateLevel('delay', v)} color="green" />
            <Fader label="WID" valueProp={levels.width} onChange={(v) => updateLevel('width', v)} color="yellow" />
            <Fader label="SPD" valueProp={levels.speed} onChange={(v) => updateLevel('speed', v)} color="yellow" />
            <Fader label="MAIN" valueProp={levels.master} onChange={(v) => updateLevel('master', v)} color="gray" />
        </div>
    );
}
